import { ShowItem } from 'interfaces/widget-types'
import {
  getShowIds,
  addShowToSelection,
  removeShowFromSelection,
  canSelectShow,
  updateCardDetailsForShows,
  initializeCardDetails,
  MAX_ITEMS
} from './showHelpers'

export type SourceType = 'Category' | 'Collection' | 'Manual'

export interface SourceTypeState {
  selectedShowIds: string[]
  showItems: ShowItem[]
  cardDetails: Record<string, any>
}

/**
 * Get the current selected show IDs based on source type
 */
export const getSelectedShowIds = (
  sourceType: SourceType,
  categoryState: SourceTypeState,
  collectionState: SourceTypeState,
  manualShowItems: ShowItem[]
): string[] => {
  switch (sourceType) {
    case 'Category':
      return categoryState.selectedShowIds
    case 'Collection':
      return collectionState.selectedShowIds
    case 'Manual':
      return getShowIds(manualShowItems)
    default:
      return []
  }
}

/**
 * Get the current show items based on source type
 */
export const getShowItems = (
  sourceType: SourceType,
  categoryState: SourceTypeState,
  collectionState: SourceTypeState,
  manualShowItems: ShowItem[]
): ShowItem[] => {
  switch (sourceType) {
    case 'Category':
      return categoryState.showItems
    case 'Collection':
      return collectionState.showItems
    case 'Manual':
      return manualShowItems
    default:
      return []
  }
}

/**
 * Get the current card details based on source type
 */
export const getCardDetails = (
  sourceType: SourceType,
  categoryState: SourceTypeState,
  collectionState: SourceTypeState,
  manualCardDetails: Record<string, any>
): Record<string, any> => {
  switch (sourceType) {
    case 'Category':
      return categoryState.cardDetails
    case 'Collection':
      return collectionState.cardDetails
    case 'Manual':
      return manualCardDetails
    default:
      return {}
  }
}

/**
 * Handle show selection for Category or Collection
 */
export const handleShowSelection = (
  show: any,
  showId: string,
  isSelected: boolean,
  currentState: SourceTypeState,
  setState: (state: SourceTypeState) => void
): void => {
  if (isSelected) {
    // Deselect show
    const updatedShowItems = removeShowFromSelection(showId, currentState.showItems)
    const updatedShowIds = currentState.selectedShowIds.filter(id => id !== showId)
    const updatedCardDetails = updateCardDetailsForShows(updatedShowIds, currentState.cardDetails)
    
    setState({
      selectedShowIds: updatedShowIds,
      showItems: updatedShowItems,
      cardDetails: updatedCardDetails
    })
  } else {
    // Select show
    if (canSelectShow(showId, currentState.selectedShowIds, MAX_ITEMS)) {
      const updatedShowItems = addShowToSelection(show, currentState.showItems, MAX_ITEMS)
      const updatedShowIds = [...currentState.selectedShowIds, showId]
      const updatedCardDetails = updateCardDetailsForShows(updatedShowIds, currentState.cardDetails)
      
      // Initialize card details for newly selected show
      initializeCardDetails([showId]).then(newDetails => {
        setState({
          selectedShowIds: updatedShowIds,
          showItems: updatedShowItems,
          cardDetails: {
            ...updatedCardDetails,
            ...newDetails
          }
        })
      })
    }
  }
}

/**
 * Update card details reactively when shows change
 */
export const updateCardDetailsReactive = async (
  selectedShowIds: string[],
  currentCardDetails: Record<string, any>,
  setCardDetails: (details: Record<string, any>) => void
): Promise<void> => {
  const updatedDetails = updateCardDetailsForShows(selectedShowIds, currentCardDetails)
  
  // Initialize card details for newly selected shows
  const newShowIds = selectedShowIds.filter(id => !currentCardDetails[id])
  if (newShowIds.length > 0) {
    const newDetails = await initializeCardDetails(newShowIds)
    setCardDetails({
      ...updatedDetails,
      ...newDetails
    })
  } else {
    // Only update if details actually changed
    const currentKeys = Object.keys(currentCardDetails).sort()
    const updatedKeys = Object.keys(updatedDetails).sort()
    if (JSON.stringify(currentKeys) !== JSON.stringify(updatedKeys)) {
      setCardDetails(updatedDetails)
    }
  }
}

/**
 * Initialize state from show items (for initialData)
 */
export const initializeStateFromShowItems = (
  showItems: ShowItem[],
  cardDetails: Record<string, any>
): SourceTypeState => {
  return {
    selectedShowIds: getShowIds(showItems),
    showItems: showItems.slice(0, MAX_ITEMS),
    cardDetails
  }
}
